'use client';

import { useEffect } from 'react';
import Link from 'next/link';

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error('App error boundary:', error);
  }, [error]);

  return (
    <section className="err-page">
      <div className="container err-inner">
        <span className="err-kicker">Something went wrong</span>
        <h1 className="err-title">We hit an unexpected error</h1>
        <p className="err-body">
          Please try again. If the problem keeps happening, our team is happy to help — just get
          in touch.
        </p>

        <div className="err-actions">
          <button type="button" onClick={reset} className="err-btn err-btn-primary">
            Try again
          </button>
          <Link href="/" className="err-btn err-btn-secondary">
            Back to home
          </Link>
          <Link href="/contact" className="err-btn err-btn-ghost">
            Contact support
          </Link>
        </div>

        {error.digest && <p className="err-digest">Reference: {error.digest}</p>}
      </div>

      <style>{`
        .err-page {
          padding: 3rem 0 5rem;
          background: var(--color-white);
          min-height: 60vh;
        }
        .err-inner {
          max-width: 720px;
          text-align: center;
        }
        .err-kicker {
          display: inline-block;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: var(--color-white);
          background: #cf3f43;
          padding: 0.25rem 0.75rem;
          border-radius: 999px;
          margin-bottom: 1rem;
        }
        .err-title {
          font-size: clamp(1.6rem, 3.5vw, 2.2rem);
          font-weight: 700;
          color: var(--color-text-primary);
          margin: 0 0 0.75rem;
          line-height: 1.2;
        }
        .err-body {
          font-size: 1rem;
          color: var(--color-text-secondary);
          line-height: 1.6;
          margin: 0 auto 1.5rem;
          max-width: 560px;
        }
        .err-actions {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }
        .err-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.8rem 1.5rem;
          border-radius: 1rem;
          font-size: 0.9rem;
          font-weight: 700;
          text-decoration: none;
          border: 2px solid transparent;
          cursor: pointer;
          transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.15s;
        }
        .err-btn-primary {
          background: var(--color-accent);
          color: var(--color-primary-dark);
        }
        .err-btn-primary:hover {
          background: var(--color-accent-dark);
          transform: translateY(-1px);
        }
        .err-btn-secondary {
          background: transparent;
          color: var(--color-primary);
          border-color: var(--color-primary);
        }
        .err-btn-secondary:hover {
          background: var(--color-primary);
          color: var(--color-white);
        }
        .err-btn-ghost {
          background: transparent;
          color: var(--color-text-secondary);
          border-color: var(--color-border);
        }
        .err-btn-ghost:hover {
          background: var(--color-off-white);
          color: var(--color-text-primary);
        }
        .err-digest {
          font-size: 0.75rem;
          color: var(--color-text-muted);
          font-family: monospace;
        }
      `}</style>
    </section>
  );
}
